/**
 * OfficerBadge - a Teams-style person avatar for an officer (Bill 2026-10-13).
 *
 * Static: a 32px round photo - the size Teams uses in its lists - with the officer's Core
 * operator initials on a coloured circle when Entra has no photo. Photo, name, title and
 * department come live from Entra (useStaffProfiles -> /v3/config/staff-directory -> Graph),
 * for any staff member, not only pilot users.
 *
 * Hover (after a short pause) or press: Teams' NATIVE profile card (TeamsJS
 * profile.showProfile). That API is beta and host-dependent; where this Teams client does not
 * support it, OfficerCard opens instead - a copy of the Teams card (photo, Chat / Video / Call,
 * quick message, contact details, Reports to). The badge sits beside the row button, never
 * inside it, so it never opens the cart.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import type { StaffDirectoryEntry } from '@retail-ops/api-client';
import { showTeamsProfile, type StaffMessageReference } from '@/services/teams';
import { OfficerCard } from './OfficerCard';

const HOVER_DELAY_MS = 450;
const CLOSE_GRACE_MS = 250;
const CARD_WIDTH = 340;
/** Room the card needs below the avatar before it opens above it instead. */
const CARD_ROOM = 440;

export interface OfficerBadgeProps {
  /** Core operator code, e.g. "CDL" (initials fallback). */
  code: string;
  /** The officer's UPN / e-mail; without it there is no profile card. */
  upn?: string | null;
  /** Live Entra profile (from useStaffProfiles); null/undefined while loading or unknown. */
  profile?: StaffDirectoryEntry | null;
  /** Shown when Entra has no name yet, e.g. the roster display name. */
  fallbackName?: string | null;
  /** Avatar diameter in px; 32 matches Teams list rows. */
  size?: number;
  /** The cart this row is about - every message from the card names it and links to it. */
  reference?: StaffMessageReference | null;
}

export function OfficerBadge({ code, upn, profile, fallbackName, size = 32, reference }: OfficerBadgeProps): JSX.Element {
  const ref = useRef<HTMLSpanElement | null>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [card, setCard] = useState<DOMRect | null>(null);

  useEffect(() => () => {
    if (openTimer.current !== null) window.clearTimeout(openTimer.current);
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
  }, []);

  // While the card is open, a press anywhere outside it (and outside the avatar) closes it -
  // needed once someone is typing a quick message, when leaving the card no longer closes it.
  useEffect(() => {
    if (!card) return undefined;
    const onDown = (e: MouseEvent | TouchEvent): void => {
      const t = e.target as Node | null;
      if (t && (cardRef.current?.contains(t) || ref.current?.contains(t))) return;
      setCard(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [card]);

  const initials = code.trim().toUpperCase();
  const name = profile?.name || fallbackName || initials;
  const email = (upn ?? '').trim();
  const label = `${name}${profile?.title ? ` - ${profile.title}` : ''}`;

  const open = async (trigger: 'MouseHover' | 'Press'): Promise<void> => {
    if (!email || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const native = await showTeamsProfile(email, rect, trigger);
    if (!native) setCard(rect);
  };
  const cancelClose = (): void => { if (closeTimer.current !== null) { window.clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = (): void => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      // Keep it open while focus is inside (typing a quick message).
      if (cardRef.current?.contains(document.activeElement)) return;
      setCard(null);
    }, CLOSE_GRACE_MS);
  };
  const onEnter = (): void => {
    cancelClose();
    if (!email || card) return;
    openTimer.current = window.setTimeout(() => void open('MouseHover'), HOVER_DELAY_MS);
  };
  const onLeave = (): void => {
    if (openTimer.current !== null) { window.clearTimeout(openTimer.current); openTimer.current = null; }
    scheduleClose();
  };
  const onKey = (e: KeyboardEvent<HTMLSpanElement>): void => {
    if (e.key === 'Escape') { setCard(null); return; }
    if (!email || (e.key !== 'Enter' && e.key !== ' ')) return;
    e.preventDefault();
    void open('Press');
  };

  return (
    <span className="officer-badge" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <span
        ref={ref}
        className={`officer-badge__avatar${profile?.photo ? ' officer-badge__avatar--photo' : ''}`}
        style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
        title={card ? undefined : label}
        aria-label={label}
        {...(email ? { role: 'button', tabIndex: 0, onClick: () => void open('Press'), onKeyDown: onKey } : {})}
      >
        {profile?.photo ? <img src={profile.photo} alt="" width={size} height={size} /> : initials}
      </span>
      {/* Portal to <body>: the Home card body is a CSS size container, which would otherwise
          become the containing block for this position:fixed card. */}
      {card && createPortal(
        <div
          ref={cardRef}
          className="officer-card"
          role="dialog"
          aria-label={`${name} profile`}
          style={{
            left: Math.max(8, Math.min(card.left, window.innerWidth - CARD_WIDTH - 8)),
            ...(card.bottom + CARD_ROOM > window.innerHeight && card.top > window.innerHeight - card.bottom
              ? { bottom: window.innerHeight - card.top + 6, maxHeight: card.top - 14 }
              : { top: card.bottom + 6, maxHeight: window.innerHeight - card.bottom - 14 }),
          }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          onKeyDown={(e) => { if (e.key === 'Escape') setCard(null); }}
        >
          <OfficerCard upn={email} fallbackName={name} fallbackInitials={initials} reference={reference}
            onClose={() => setCard(null)} />
        </div>,
        document.body,
      )}
    </span>
  );
}
